import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, GripVertical, Pencil, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "@/lib/toast";

const YELLOW = "#F9BC26"; // Tâches module accent

/**
 * ChecklistEditor — Spec iter208 (2026-02-28).
 *
 * Aligned with the creation dialog pattern (`ChecklistRow` in
 * `TaskCreateDialog.jsx`) : each row is draggable (HTML5 native), the
 * label is editable inline (click or ✎), and the ordering is persisted
 * server-side via `PATCH /tasks/{id}/checklist/reorder`.
 *
 * Rappel produit : la checklist est un **outil de suivi optionnel**. Elle
 * n'a JAMAIS bloqué la clôture d'une tâche côté backend, mais l'affichage
 * du compteur "0/N" pouvait le laisser penser — d'où le petit hint
 * discret sous le titre.
 */
export default function ChecklistEditor({ task, onChanged, frozen = false }) {
  const [items, setItems] = useState(task.checklist || []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  // Sync local items whenever the parent task changes (fixes stale state
  // after edit-dialog patch or refresh).
  useEffect(() => {
    setItems(task.checklist || []);
  }, [task?.id, task?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const v = input.trim();
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/checklist`, { label: v });
      setItems(data.checklist || []);
      onChanged?.(data);
      setInput("");
    } catch (err) {
      toast.error("Ajout impossible", { description: err.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };

  const toggle = async (id, done) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}/checklist/${id}`, { done });
      setItems(data.checklist || []);
      onChanged?.(data);
    } catch (err) {
      toast.error("Mise à jour refusée", { description: err.response?.data?.detail || "" });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tasks/${task.id}/checklist/${id}`);
      const next = items.filter((i) => i.id !== id);
      setItems(next);
      onChanged?.({ ...task, checklist: next });
    } catch (err) {
      toast.error("Suppression refusée", { description: err.response?.data?.detail || "" });
    }
  };

  const rename = async (id, label) => {
    // Best-effort inline rename : on efface l'item + on le recrée avec le
    // nouveau libellé à la même position. Simplifie le contrat backend :
    // pas besoin d'un endpoint dédié PATCH label — la reorder finale
    // remet le tout en ordre. En cas d'échec, on rollback UI.
    const item = items.find((i) => i.id === id);
    if (!item || !label.trim() || label === item.label) return;
    const trimmed = label.trim();
    try {
      // Étape 1 : ajouter le nouvel item
      const { data: added } = await api.post(`/tasks/${task.id}/checklist`, { label: trimmed });
      const newId = (added.checklist || []).slice(-1)[0]?.id;
      // Étape 2 : supprimer l'ancien
      await api.delete(`/tasks/${task.id}/checklist/${id}`);
      // Étape 3 : reprendre l'ordre en insérant newId à la position de id
      const original = items.map((i) => (i.id === id ? newId : i.id)).filter(Boolean);
      const { data: reordered } = await api.patch(`/tasks/${task.id}/checklist/reorder`, {
        item_ids: original,
      });
      setItems(reordered.checklist || []);
      onChanged?.(reordered);
    } catch (err) {
      toast.error("Renommage impossible", { description: err.response?.data?.detail || "" });
    }
  };

  const move = async (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    // Optimistic UI: apply locally then persist.
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    try {
      const { data } = await api.patch(`/tasks/${task.id}/checklist/reorder`, {
        item_ids: next.map((i) => i.id),
      });
      setItems(data.checklist || next);
      onChanged?.(data);
    } catch (err) {
      // Rollback on error
      setItems(items);
      toast.error("Réordonnancement impossible", { description: err.response?.data?.detail || "" });
    }
  };

  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <div
      className="rounded-md border border-[#3A3A3A] p-4 bg-white/[0.02] shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
      data-testid="checklist-editor"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Checklist</h3>
        <span className="text-xs text-muted-foreground tabular-nums">{done}/{items.length} · {pct}%</span>
      </div>

      {/* Iter208 — hint discret : la checklist est un OUTIL DE SUIVI,
          elle ne bloque JAMAIS la clôture (backend a été vérifié : aucun
          gate). Évite la confusion mentale utilisateur. */}
      <div
        className="flex items-start gap-1.5 mb-3 text-[10.5px] text-muted-foreground leading-relaxed"
        style={{ opacity: 0.75 }}
      >
        <Info size={11} className="shrink-0 mt-[1px]" style={{ color: YELLOW }} />
        <span>Outil de suivi optionnel — n'empêche jamais la clôture de la tâche.</span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden mb-3">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: YELLOW }} />
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mb-3">Aucune étape.</p>
      ) : (
        <ul
          className="rounded-lg p-1 space-y-0.5 mb-3"
          style={{ background: "#1e1e20" }}
          data-testid="checklist-list"
        >
          {items.map((it, idx) => (
            <ChecklistRow
              key={it.id}
              index={idx}
              item={it}
              onToggle={(v) => toggle(it.id, v)}
              onRemove={() => remove(it.id)}
              onRename={(label) => rename(it.id, label)}
              onMove={move}
              frozen={frozen}
            />
          ))}
        </ul>
      )}

      {!frozen && <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ajouter une étape…"
          disabled={busy}
          data-testid="checklist-input"
        />
        <Button
          onClick={add}
          variant="outline"
          disabled={busy || !input.trim()}
          data-testid="checklist-add"
        >
          <Plus size={14} />
        </Button>
      </div>}
    </div>
  );
}

/**
 * ChecklistRow — Drag-and-drop row (HTML5 natif) + toggle + inline edit.
 * Mirror of the pattern used in `TaskCreateDialog.jsx`.
 */
function ChecklistRow({ index, item, onToggle, onRemove, onRename, onMove, frozen = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.label);
  const [dragOver, setDragOver] = useState(false);
  const rowRef = useRef(null);

  // Sync draft with prop when item.label changes remotely.
  useEffect(() => { setDraft(item.label); }, [item.label]);

  const commit = () => {
    const v = draft.trim();
    if (!v) { setDraft(item.label); setEditing(false); return; }
    if (v !== item.label) onRename(v);
    setEditing(false);
  };
  const cancel = () => { setDraft(item.label); setEditing(false); };

  return (
    <li
      ref={rowRef}
      draggable={!editing && !frozen}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(index));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (!Number.isNaN(from)) onMove(from, index);
      }}
      className="group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors"
      style={{
        background: dragOver ? "rgba(249,188,38,0.10)" : "transparent",
        border: dragOver ? "1px dashed rgba(249,188,38,0.4)" : "1px solid transparent",
        cursor: editing ? "default" : "grab",
      }}
      data-testid={`checklist-item-${item.id}`}
    >
      <span
        className="flex-shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
        aria-hidden="true"
        title="Glisser pour réordonner"
      >
        <GripVertical size={14} />
      </span>
      <Checkbox
        checked={item.done}
        disabled={frozen}
        onCheckedChange={(v) => onToggle(!!v)}
        className="data-[state=checked]:bg-[#F9BC26] data-[state=checked]:border-[#F9BC26] data-[state=checked]:text-[#1B1B1B]"
        data-testid={`checklist-toggle-${item.id}`}
      />
      {editing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          onBlur={commit}
          className="flex-1 h-7 text-sm"
          data-testid={`checklist-input-edit-${item.id}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`flex-1 text-left text-sm truncate hover:text-white transition-colors ${item.done ? "line-through text-muted-foreground" : ""}`}
          title={frozen ? undefined : "Cliquer pour modifier"}
          disabled={frozen}
          data-testid={`checklist-text-${item.id}`}
        >
          {item.label}
        </button>
      )}
      {!frozen && <button
        type="button"
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-white"
        aria-label="Modifier"
        title="Modifier"
        data-testid={`checklist-edit-${item.id}`}
      >
        <Pencil size={13} />
      </button>}
      {!frozen && <button
        type="button"
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-status-danger"
        aria-label="Retirer"
        title="Retirer"
        data-testid={`checklist-remove-${item.id}`}
      >
        <Trash2 size={13} />
      </button>}
    </li>
  );
}
